import React, { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import styles from "./NavBar.module.css";
import Image from "next/image";
import MobileSidebar from "./MobileSidebar";
import FlexibleMegamenu from "./FlexibleMegamenu";
import { handleLinkClick } from "@/Config/helper";
import CustomiseCta from "../CustomiseCta/CustomiseCta";
import { fetchMegamenuData } from "@/services/CmsService";
import { fetchCommonData } from "@/services/CommonService";
import {
  getLocaleFromPath,
  removeCountryPrefix,
  KNOWN_COUNTRIES,
  GLOBAL_ONLY_PAGES,
  getLocaleFromCountry,
  ensureLocalesLoaded,
  COUNTRY_LOCALE_MAP,
  COMMON_COUNTRIES,
} from "@/Config/countryConstants";
import {
  getSelectedCountryByUser,
  setStayGlobal,
  setSelectedCountryByUser,
} from "@/utils/cookieutils";

interface NavBarTwoProps {
  handleEnrollButtonClick: () => void;
  handleBroutcherButtonClick: (broutcherLink?: string) => void;
  pageTitle?: any;
  data?: any;
  megamenuData?: any;
  popUpData?: any;
}

const NavTwo: React.FC<NavBarTwoProps> = ({
  handleBroutcherButtonClick,
  pageTitle,
  data,
  megamenuData: serverMegamenuData,
  popUpData,
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [megamenuData, setMegamenuData] = useState<any>(null);
  const [fallbackNavData, setFallbackNavData] = useState<any>(null);
  const [isClient, setIsClient] = useState(false);
  const router = useRouter();
  // Read cookie on each render so effects can react when it changes
  const selectedCountryCookie = getSelectedCountryByUser();

  // Get current country from path
  const getCurrentCountry = () => {
    const pathSegments = router.asPath.split("/").filter(Boolean);
    return KNOWN_COUNTRIES.includes(pathSegments[0]) ? pathSegments[0] : null;
  };

  const currentCountry = getCurrentCountry();

  // Check if current page is a global-only page (no country-specific versions)
  const isGlobalOnlyPage = () => {
    const pathSegments = router.asPath.split("/").filter(Boolean);
    const cleanPath = pathSegments[0] || "";
    return GLOBAL_ONLY_PAGES.includes(cleanPath);
  };

  // Case-insensitive check for labels like "Country", "country", "COUNTRY-India", etc. (following corporate education pattern)
  const isCountryMenuLabel = (label?: string) => {
    if (!label) return false;
    const normalized = label.trim().toLowerCase();
    return normalized === "country" || normalized.startsWith("country");
  };

  // Selected country label suffix for Country menu (derive from locale, e.g. en-IN -> IN)
  const getSelectedCountryLabelSuffix = () => {
    // Only show suffix on client side to prevent hydration mismatch
    if (!isClient) return "";

    const isoFromCountry = (country?: string): string => {
      if (!country) return "";
      const locale = getLocaleFromCountry(country);
      if (!locale || locale.toLowerCase() === "en") return "";
      const parts = locale.split("-");
      return parts[1]?.toUpperCase() || "";
    };
    const code = isoFromCountry(currentCountry || "south-africa");
    if (!code) return "";
    return ` (${code})`;
  };

  // Custom click handler for country dropdown items
  const handleCountryDropdownClick = (
    e: React.MouseEvent,
    url: string,
    countryItem: any
  ) => {
    e.preventDefault();
    setDropdownOpen(null);

    // Check if this is South Africa (Global) selection
    const isSouthAfrica =
      url === "/" ||
      url === "" ||
      countryItem?.title?.toLowerCase().includes("south africa") ||
      countryItem?.title?.toLowerCase().includes("global");

    if (isSouthAfrica) {
      // User selected South Africa (Global)
      setStayGlobal();
    } else {
      // Extract country from URL and set cookie
      const countryFromUrl = url.replace(/^\//, "").split("/")[0];
      if (KNOWN_COUNTRIES.includes(countryFromUrl)) {
        setSelectedCountryByUser(countryFromUrl);
      }
    }

    router.push(url);
  };

  // Simple function to manipulate country links - convert external URLs to internal routes
  // Helper function to clean URLs that might have double slashes
  const cleanUrl = (url: string): string => {
    if (!url) return "#";
    // Remove leading slash if URL starts with /https:// or /http://
    if (url.startsWith("/https://") || url.startsWith("/http://")) {
      return url.substring(1);
    }
    return url;
  };

  const manipulateCountryLinks = (navData: any) => {
    if (!navData || !navData.main_nav_links) return navData;

    const currentPath = router.asPath;
    const cleanPath = currentPath?.split("?")[0]?.split("#")[0];

    // Remove country prefix to get the base path
    const pathWithoutCountry = removeCountryPrefix(cleanPath);

    // Preserve path for ANY non-root page (e.g., "/master-of-business-administration" -> "/india/master-of-business-administration").
    // Root ("/") goes to country root (e.g., "/india").
    const isPreservePathPage = pathWithoutCountry !== "/";

    // Clone the navigation data to avoid mutating the original
    const manipulatedData = JSON.parse(JSON.stringify(navData));

    // Process main navigation links
    manipulatedData.main_nav_links = manipulatedData?.main_nav_links?.map(
      (item: any) => {
        // Handle Country dropdown - check if this is the Country item
        if (isCountryMenuLabel(item?.label) && item?.nested_list_links) {
          const manipulatedCountryLinks = item.nested_list_links.map(
            (countryItem: any) => {
              // Skip URL manipulation for external URLs (containing http/https)
              if (
                countryItem.url &&
                (countryItem.url.startsWith("http://") ||
                  countryItem.url.startsWith("https://"))
              ) {
                return countryItem; // Return unchanged for external URLs
              }

              // Extract country code from URL (e.g., "nigeria" from "https://www.nigeria.regenesys.net/")
              let countryCode = "";

              // Normalize and branch by URL type
              const raw = (countryItem.url || "").trim();
              if (!raw) {
                countryCode = "";
              } else if (raw.startsWith("/")) {
                // Relative path: "/india"
                countryCode = raw.replace("/", "").toLowerCase();
              } else if (/^(https?:)?\/\//i.test(raw)) {
                // Absolute or protocol-relative URL: "https://nigeria.regenesys.net" or "//nigeria.regenesys.net"
                try {
                  const absolute = raw.startsWith("//") ? `https:${raw}` : raw;
                  const url = new URL(absolute);
                  const hostname = url.hostname;
                  const subdomain = hostname.split(".")[0];
                  countryCode = subdomain.toLowerCase();
                } catch {
                  const match = raw.match(/https?:\/\/www\.([^.]+)\./i);
                  countryCode = match ? match[1].toLowerCase() : "";
                }
              } else {
                // Bare slug like 'india' or 'kenya'
                countryCode = raw.toLowerCase();
              }

              // Fallback: If no country code from URL, try to extract from title
              if (!countryCode && countryItem.title) {
                const titleLower = countryItem.title.toLowerCase().trim();
                // Check if title matches any known country
                const matchedCountry = KNOWN_COUNTRIES.find(
                  (country) =>
                    titleLower === country ||
                    titleLower === country.replace("-", " ") ||
                    titleLower.includes(country)
                );
                if (matchedCountry) {
                  countryCode = matchedCountry;
                }
              }

              // Handle South Africa (Global) - check by URL being '/'
              const isSouthAfrica =
                countryItem.url === "/" ||
                countryItem.url === "" ||
                countryCode === "south-africa";

              // Common country codes that should be recognized even without locale mapping
              
              // Check if country is valid: either in KNOWN_COUNTRIES (from CMS) or in COMMON_COUNTRIES
              const isValidCountry = countryCode && (
                KNOWN_COUNTRIES?.includes(countryCode) || 
                COMMON_COUNTRIES?.includes(countryCode)
              );
              
              // Check if TARGET country has locale mapping - only manipulate if locale exists
              const hasLocaleMapping = isValidCountry && COUNTRY_LOCALE_MAP[countryCode];

              if (isSouthAfrica) {
                // For South Africa, use the same logic as other countries
                if (isPreservePathPage) {
                  // For non-root pages, preserve the path without country prefix
                  countryItem.url = pathWithoutCountry;
                } else {
                  // For root page, go to root
                  countryItem.url = "/";
                }
              } else if (!countryCode || !isValidCountry) {
                // If no valid country code found or not a recognized country, keep original URL or use fallback
                countryItem.url = countryItem.url || "/";
              } else if (hasLocaleMapping) {
                // Only manipulate URL if TARGET country is valid AND has locale mapping
                if (isPreservePathPage) {
                  // For events, programs, schools pages, preserve the full path
                  countryItem.url = `/${countryCode}${pathWithoutCountry}`;
                } else {
                  // For other pages, use country root
                  countryItem.url = `/${countryCode}`;
                }
              } else {
                // If country is valid but has no locale mapping, keep original URL from backend (no manipulation)
                countryItem.url = countryItem.url;
              }

              return countryItem;
            }
          );

          item.nested_list_links = manipulatedCountryLinks;
        }
        return item;
      }
    );

    return manipulatedData;
  };

  const feesBrochure = data?.fees_brouchure?.pdf_link?.url || "";
  const prospectusBrochure = data?.prospectous_brouchure?.pdf_link?.url || "";
  const isAlumniPage = router.pathname === "/alumni";
  // Determine if current route is events listing or an event detail page
  const isEventsRoute = (() => {
    const cleanPath = (router.asPath || "/").split("?")[0].split("#")[0];
    const pathWithoutCountry = removeCountryPrefix(cleanPath);
    return (
      router.pathname === "/events" ||
      router.pathname === "/events/[slug]" ||
      pathWithoutCountry === "/events" ||
      pathWithoutCountry.startsWith("/events/")
    );
  })();

  // Transform megamenu data to expected format
  // Handles both server-side data (direct mega_menu_header object) and client-side API response
  const transformMegamenuData = (data: any) => {
    // Handle server-side data: data is already mega_menu_header object
    // Handle client-side data: data has mega_menu_header property
    const megaMenuHeader = data?.mega_menu_header || data;
    
    const hasExpectedStructure =
      megaMenuHeader?.main_nav_links &&
      Array.isArray(megaMenuHeader.main_nav_links);

    if (hasExpectedStructure) {
      return {
        categories: megaMenuHeader.main_nav_links || [],
        certificationCourses: megaMenuHeader?.certification_courses || {
          title: "",
          url: "",
          items: [],
        },
      };
    } else {
      const fallbackData = {
        categories: megaMenuHeader?.main_nav_links || [],
        certificationCourses: megaMenuHeader?.certification_courses || {
          title: "",
          url: "",
          items: [],
        },
      };
      if (
        fallbackData.categories.length > 0 ||
        (fallbackData.certificationCourses.title &&
          fallbackData.certificationCourses.title.trim() !== "")
      ) {
        return fallbackData;
      }
      return null;
    }
  };

  // Initialize megamenu data from server-side props if available
  useEffect(() => {
    if (serverMegamenuData) {
      const transformed = transformMegamenuData(serverMegamenuData);
      if (transformed) {
        setMegamenuData(transformed);
        // Server-side data is available, no need to fetch client-side
        return;
      }
    }
  }, [serverMegamenuData]);

  useEffect(() => {
    // Only fetch if server-side data is not available
    if (serverMegamenuData) {
      return; // Skip client-side fetch if server data exists
    }

    const fetchMegamenu = async () => {
      try {
        // Ensure locales are loaded before determining locale
        await ensureLocalesLoaded();

        // Determine locale: prefer SelectedCountryByUser if present
        const cleanPath = (router.asPath || "/").split("?")[0].split("#")[0];
        const cookieCountry = getSelectedCountryByUser();
        const locale =
          cookieCountry && cookieCountry !== "south-africa"
            ? getLocaleFromCountry(cookieCountry)
            : getLocaleFromPath(cleanPath);
        const data = await fetchMegamenuData(locale);
        const apiData = data?.data || data;

        const transformed = transformMegamenuData(apiData);
        if (transformed) {
          setMegamenuData(transformed);
        } else {
          setMegamenuData(null);
        }
      } catch (error: any) {
        console.error("Error fetching megamenu data:", error);
        setMegamenuData({
          categories: [],
          certificationCourses: {
            title: "",
            url: "",
            items: [],
          },
        });
      }
    };

    fetchMegamenu();
  }, [router.asPath, selectedCountryCookie, serverMegamenuData]);

  useEffect(() => {
    const fetchFallbackNavData = async () => {
      try {
        const currentPath = router.asPath;
        const cleanPath = currentPath.split("?")[0].split("#")[0];
        const cookieCountry = getSelectedCountryByUser();
        const effectiveLocale =
          cookieCountry && cookieCountry !== "south-africa"
            ? getLocaleFromCountry(cookieCountry)
            : getLocaleFromPath(cleanPath);

        if (
          effectiveLocale !== getLocaleFromPath(cleanPath) ||
          !data?.main_nav_links
        ) {
          const commonData = await fetchCommonData(effectiveLocale);
          setFallbackNavData(commonData?.data?.header);
        } else {
          setFallbackNavData(null);
        }
      } catch (error: any) {
        console.error("Error fetching fallback nav data:", error);
      }
    };

    fetchFallbackNavData();
  }, [data?.main_nav_links, router.asPath, selectedCountryCookie]);

  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    if (typeof window !== "undefined") {
      checkIfMobile();
      window.addEventListener("resize", checkIfMobile);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("resize", checkIfMobile);
      }
    };
  }, []);

  // Ensure locales are loaded on client-side
  useEffect(() => {
    ensureLocalesLoaded().catch(() => {});
  }, []);

  // Set client flag after hydration to prevent hydration mismatch
  useEffect(() => {
    setIsClient(true);
  }, []);

  const logoAlumniWidth = isAlumniPage
    ? {
        url: "/assets/alumni-logo.svg",
        alternativeText: "Regenesys Alumni Logo",
        width: isMobile ? 142 : 204,
        height: isMobile ? 34 : 49,
      }
    : {
        url: data?.logo?.url,
        alternativeText: data?.logo?.alternativeText || "Regenesys Logo",
        width: isMobile ? 142 : 204,
        height: isMobile ? 28 : 40,
      };
  const logo =
    router.pathname === "/alumni"
      ? {
          url: "/assets/Regenesys_Alumni_Logo_White.png",
          alternativeText: "Regenesys Alumni Logo",
        }
      : {
          url: data?.logo?.url,
          alternativeText: data?.logo?.alternativeText || "Regenesys Logo",
        };

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    if (typeof window !== "undefined") {
      window.addEventListener("scroll", handleScroll);

      const handleRouteChange = () => {
        setDropdownOpen(null);
      };

      router.events.on("routeChangeStart", handleRouteChange);

      return () => {
        window.removeEventListener("scroll", handleScroll);
        router.events.off("routeChangeStart", handleRouteChange);
      };
    }
  }, [router.events]);

  const handleMouseEnter = (index: number) => {
    setDropdownOpen(index);
  };

  const handleMouseLeave = () => {
    setDropdownOpen(null);
  };

  const handleCloseDropdown = () => {
    setDropdownOpen(null);
  };

  return (
    <>
      <div className={styles.mainNavContainer}>
        <div
          className={`${styles.navTwoContainer} ${
            scrolled ? styles.scrolled : ""
          }`}
          style={{
            backgroundColor:
              pageTitle === "404 Error" || isEventsRoute ? "#18463e" : "none",
          }}
        >
          <div className={styles.navContent}>
            <div className={styles.logoContainer}>
              <Link
                href={cleanUrl(
                  (data as any)?.logo_url || (data as any)?.logo?.link || "/"
                )}
                title={logo.alternativeText}
              >
                <Image
                  title={logo.alternativeText}
                  src={logo.url}
                  alt={logo.alternativeText}
                  width={204}
                  height={40}
                  className={styles.LogoImg}
                  style={{
                    width: logoAlumniWidth.width,
                    height: logoAlumniWidth.height,
                  }}
                />
              </Link>
            </div>

            <div className={styles.mobileApplyButton}>
              <CustomiseCta
                btnLabelDesktop="Study Enquiry"
                position="Top"
                ctaData={data?.study_enquiry_cta}
                preEnrollmentEnable
                mixpanelEvent="study_enquiry_clicked"
                popupData={popUpData}
              />
            </div>

            <nav className={styles.navTwo}>
              {manipulateCountryLinks(
                data || fallbackNavData
              )?.main_nav_links?.map((item: any, index: number) => {
                // Hide country dropdown on global-only pages
                if (isCountryMenuLabel(item?.label) && isGlobalOnlyPage()) {
                  return null;
                }

                return (
                  <div
                    key={index}
                    className={`${styles.navItem} ${
                      item?.nested_list_links?.length > 0 ||
                      (item?.label === "Study at Regenesys" && megamenuData)
                        ? styles.hasSubmenu
                        : ""
                    }`}
                    onMouseEnter={() => handleMouseEnter(index)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <div onClick={() => setDropdownOpen(null)}>
                      <Link
                        href={cleanUrl(item?.url)}
                        className={
                          item?.label === "Apply"
                            ? styles.applyButton
                            : styles.navLinkTwo
                        }
                        aria-label={
                          isCountryMenuLabel(item?.label)
                            ? `${item?.label}${getSelectedCountryLabelSuffix()}`
                            : item?.label
                        }
                        title={
                          isCountryMenuLabel(item?.label)
                            ? `${item?.label}${getSelectedCountryLabelSuffix()}`
                            : item?.label
                        }
                      >
                        {isCountryMenuLabel(item?.label)
                          ? `${item?.label}${getSelectedCountryLabelSuffix()}`
                          : item?.label}
                        {(item?.nested_list_links?.length > 0 ||
                          (item?.label === "Study at Regenesys" &&
                            megamenuData)) && (
                          <Image
                            src="/assets/DropDownArrow.svg"
                            alt="DropDown Arrow"
                            title="DropDown Arrow"
                            width={12}
                            height={7}
                            className={`${styles.dropDownImg} ${
                              dropdownOpen === index ? styles.rotateArrow : ""
                            }`}
                          />
                        )}
                      </Link>
                    </div>

                    {(item?.nested_list_links?.length > 0 ||
                      (item?.label === "Study at Regenesys" &&
                        megamenuData)) && (
                      <div
                        className={
                          item?.label === "Study at Regenesys"
                            ? styles.submenu
                            : styles.submenuDropDown
                        }
                        onMouseEnter={() => handleMouseEnter(index)}
                        onMouseLeave={handleMouseLeave}
                      >
                        <div className={styles.submenuItems}>
                          {item?.label === "Study at Regenesys" ? (
                            <FlexibleMegamenu
                              onClose={handleCloseDropdown}
                              megamenuData={megamenuData}
                            />
                          ) : (
                            <div
                              className={styles.megamenuFlexDropDown}
                              style={{
                                display: "block",
                              }}
                            >
                              {item?.nested_list_links?.map(
                                (subItem: any, subIndex: number) => {
                                  const isEducation =
                                    subItem?.title?.toLowerCase() ===
                                    "school of education";
                                  const isPublicManagement =
                                    subItem?.title?.toLowerCase() ===
                                    "school of public management";

                                  if (isEducation) return null;

                                  return (
                                    <div
                                      key={subIndex}
                                      className={styles.submenuSection}
                                    >
                                      {subItem?.title === "Fees" ||
                                      subItem?.title === "Prospectus" ? (
                                        <button
                                          onClick={() => {
                                            handleBroutcherButtonClick(
                                              subItem?.title === "Fees"
                                                ? feesBrochure
                                                : prospectusBrochure
                                            );
                                            setDropdownOpen(null);
                                          }}
                                          className={styles.submenuTitleButton}
                                        >
                                          {subItem?.title}
                                        </button>
                                      ) : (
                                        <div
                                          onClick={() => setDropdownOpen(null)}
                                        >
                                          <Link
                                            href={cleanUrl(subItem?.url)}
                                            title={subItem?.title}
                                            className={styles.submenuTitle}
                                            onClick={(e) => {
                                              // Use custom handler for country dropdown items
                                              if (
                                                isCountryMenuLabel(item?.label)
                                              ) {
                                                handleCountryDropdownClick(
                                                  e,
                                                  cleanUrl(subItem?.url),
                                                  subItem
                                                );
                                              } else {
                                                handleLinkClick(
                                                  e,
                                                  cleanUrl(subItem?.url),
                                                  router
                                                );
                                              }
                                            }}
                                          >
                                            <h3>{subItem?.title}</h3>
                                          </Link>
                                        </div>
                                      )}

                                      {subItem?.lists?.map(
                                        (
                                          section: any,
                                          sectionIndex: number
                                        ) => (
                                          <div
                                            key={sectionIndex}
                                            className={styles.sectionMenuItems}
                                          >
                                            <Link
                                              href={cleanUrl(section?.url)}
                                              className={styles.sectionTitle}
                                              title={section?.title}
                                              onClick={() =>
                                                setDropdownOpen(null)
                                              }
                                            >
                                              <h4>{section?.title}</h4>
                                            </Link>
                                            {section?.items?.map(
                                              (
                                                item: any,
                                                itemIndex: number
                                              ) => (
                                                <Link
                                                  key={itemIndex}
                                                  href={cleanUrl(item?.url)}
                                                  className={styles.submenuItem}
                                                  title={item?.label}
                                                  onClick={() =>
                                                    setDropdownOpen(null)
                                                  }
                                                >
                                                  {item?.label}
                                                </Link>
                                              )
                                            )}
                                          </div>
                                        )
                                      )}

                                      {subItem?.links?.map(
                                        (linkItem: any, linkIndex: number) => (
                                          <Link
                                            key={linkIndex}
                                            href={cleanUrl(linkItem?.url)}
                                            title={linkItem?.label}
                                            className={styles.submenuItem}
                                            onClick={() =>
                                              setDropdownOpen(null)
                                            }
                                          >
                                            {linkItem?.label}
                                          </Link>
                                        )
                                      )}

                                      {isPublicManagement &&
                                        item?.nested_list_links
                                          ?.filter(
                                            (n: any) =>
                                              n?.title?.toLowerCase() ===
                                              "school of education"
                                          )
                                          ?.map((eduBlock: any) => (
                                            <div
                                              key={eduBlock?.id}
                                              style={{ marginTop: 10 }}
                                            >
                                              <Link
                                                href={cleanUrl(eduBlock?.url)}
                                                title={eduBlock?.title}
                                                className={styles.submenuTitle}
                                                onClick={(e) => {
                                                  // Use custom handler for country dropdown items
                                                  if (
                                                    isCountryMenuLabel(
                                                      item?.label
                                                    )
                                                  ) {
                                                    handleCountryDropdownClick(
                                                      e,
                                                      cleanUrl(eduBlock?.url),
                                                      eduBlock
                                                    );
                                                  } else {
                                                    handleLinkClick(
                                                      e,
                                                      cleanUrl(eduBlock?.url),
                                                      router
                                                    );
                                                  }
                                                }}
                                              >
                                                <h3>{eduBlock?.title}</h3>
                                              </Link>

                                              {eduBlock?.lists?.map(
                                                (
                                                  section: any,
                                                  sectionIndex: number
                                                ) => (
                                                  <div
                                                    key={sectionIndex}
                                                    className={
                                                      styles.sectionMenuItems
                                                    }
                                                  >
                                                    <Link
                                                      href={cleanUrl(
                                                        section?.url
                                                      )}
                                                      className={
                                                        styles.sectionTitle
                                                      }
                                                      title={section?.title}
                                                      onClick={() =>
                                                        setDropdownOpen(null)
                                                      }
                                                    >
                                                      <h4>{section?.title}</h4>
                                                    </Link>
                                                    {section?.items?.map(
                                                      (
                                                        item: any,
                                                        itemIndex: number
                                                      ) => (
                                                        <Link
                                                          key={itemIndex}
                                                          href={cleanUrl(
                                                            item?.url
                                                          )}
                                                          title={item?.label}
                                                          className={
                                                            styles.submenuItem
                                                          }
                                                          onClick={() =>
                                                            setDropdownOpen(
                                                              null
                                                            )
                                                          }
                                                        >
                                                          {item?.label}
                                                        </Link>
                                                      )
                                                    )}
                                                  </div>
                                                )
                                              )}

                                              {eduBlock?.links?.map(
                                                (link: any, i: number) => (
                                                  <Link
                                                    key={i}
                                                    href={cleanUrl(link?.url)}
                                                    title={link?.label}
                                                    className={
                                                      styles.submenuItem
                                                    }
                                                    onClick={() =>
                                                      setDropdownOpen(null)
                                                    }
                                                  >
                                                    {link?.label}
                                                  </Link>
                                                )
                                              )}
                                            </div>
                                          ))}
                                    </div>
                                  );
                                }
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              <div className={styles.navItem}>
                <div
                  className={styles.applyButton}
                  data-interaction="Nav CTA Click"
                >
                  <CustomiseCta
                    btnLabelDesktop="Study Enquiry"
                    position="Top"
                    ctaData={data?.study_enquiry_cta}
                    popupData={popUpData}
                    preEnrollmentEnable
                    mixpanelEvent="study_enquiry_clicked"
                  />
                </div>
              </div>
            </nav>
          </div>
        </div>
        <MobileSidebar
          apiData={
            manipulateCountryLinks(data || fallbackNavData)?.main_nav_links
          }
          data={data || fallbackNavData}
          handleBroutcherButtonClick={handleBroutcherButtonClick}
          feesBrochure={feesBrochure}
          prospectusBrochure={prospectusBrochure}
          megamenuData={megamenuData}
        />
      </div>
    </>
  );
};

export default NavTwo;
